import type { ReactNode } from "react";

/**
 * Bulk Upload Types
 *
 * Type definitions for the bulk upload feature that allows users to
 * upload multiple documents at once with progress tracking.
 */

export type BulkUploadFileStatus =
  | "pending" // Waiting in queue
  | "uploading" // Being uploaded to edge function
  | "processing" // Edge function processing (parsing/optimization)
  | "completed" // Fully processed and in KB
  | "failed"; // Error occurred

export type BulkUploadFileType = "pdf" | "doc" | "docx" | "txt" | "csv" | "image";

export interface BulkUploadFile {
  /** Unique identifier for tracking this file in the queue */
  id: string;
  /** Original File object */
  file: File;
  /** Display name */
  name: string;
  /** File size in bytes */
  size: number;
  /** Detected file type */
  type: BulkUploadFileType;
  /** Current processing status */
  status: BulkUploadFileStatus;
  /** Processing progress 0-100 */
  progress: number;
  /** Database document ID after successful upload */
  documentId?: string;
  /** Error message if upload/processing failed */
  error?: string;
  /** Timestamp when file was added to queue */
  addedAt: number;
}

export interface BulkUploadState {
  /** Files in the upload queue */
  files: BulkUploadFile[];
  /** Whether the queue is actively processing */
  isProcessing: boolean;
  /** Whether processing is paused */
  isPaused: boolean;
  /** ID of the file currently being processed */
  currentFileId: string | null;
  /** Unique batch identifier */
  batchId: string;
  /** Optional agent to attach all documents to */
  agentId?: string;
  /** Display name of the selected agent */
  agentName?: string;
  /** Timestamp when batch started processing */
  startedAt?: number;
  /** Timestamp when batch completed */
  completedAt?: number;
}

export interface BulkUploadProgress {
  /** Total number of files in batch */
  total: number;
  /** Number of successfully completed files */
  completed: number;
  /** Number of failed files */
  failed: number;
  /** Number of files currently processing */
  inProgress: number;
  /** Number of files waiting to be processed */
  pending: number;
  /** Overall progress percentage 0-100 */
  overallProgress: number;
}

export interface BulkUploadValidationResult {
  /** Whether all files passed validation */
  valid: boolean;
  /** Critical errors that prevent upload */
  errors: string[];
  /** Non-critical warnings */
  warnings: string[];
  /** Files that passed validation */
  validFiles: File[];
  /** Files that failed validation with reasons */
  invalidFiles: Array<{ file: File; reason: string }>;
}

export interface BulkUploadResult {
  /** Unique batch identifier */
  batchId: string;
  /** Total files attempted */
  totalFiles: number;
  /** Number of successful uploads */
  successCount: number;
  /** Number of failed uploads */
  failedCount: number;
  /** Total duration in milliseconds */
  duration: number;
  /** Details for each document */
  documents: Array<{
    id: string;
    name: string;
    status: "completed" | "failed";
    error?: string;
  }>;
}

/** File type info returned by detection function */
export interface FileTypeInfo {
  /** Detected file type */
  type: BulkUploadFileType;
  /** Action to send to doc-upload edge function */
  action: "upload-pdf" | "extract-document" | "upload-image";
}

/** File size warning threshold (20MB) */
export const FILE_SIZE_WARNING_THRESHOLD = 20 * 1024 * 1024;

/** Maximum recommended batch size */
export const MAX_BATCH_SIZE = 50;

export interface AgentOption {
  id: string;
  elevenlabs_id: string;
  name: string;
}

export type BulkUploadAction =
  | { type: "ADD_FILES"; payload: BulkUploadFile[] }
  | { type: "REMOVE_FILE"; payload: string }
  | { type: "UPDATE_FILE"; payload: { id: string; updates: Partial<BulkUploadFile> } }
  | { type: "SET_PROCESSING"; payload: boolean }
  | { type: "SET_PAUSED"; payload: boolean }
  | { type: "SET_AGENT"; payload: { id?: string; name?: string } }
  | { type: "CLEAR_QUEUE" }
  | { type: "CLEAR_COMPLETED" }
  | { type: "RETRY_FAILED" }
  | { type: "RESET" };

export interface BulkUploadContextType {
  state: BulkUploadState;
  progress: BulkUploadProgress;
  isActive: boolean;
  isPanelOpen: boolean;
  availableAgents: AgentOption[];
  addFiles: (files: FileList | File[]) => BulkUploadValidationResult;
  removeFile: (fileId: string) => void;
  clearQueue: () => void;
  clearCompleted: () => void;
  startUpload: () => void;
  pauseUpload: () => void;
  resumeUpload: () => void;
  retryFailed: () => void;
  setAgent: (agent: { id?: string; name?: string }) => void;
  setAvailableAgents: (agents: AgentOption[]) => void;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
}

export interface BulkUploadProviderProps {
  children: ReactNode;
}
